/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IActionContext,
    IComponentProps,
    IGridSettings,
    IImageSettings,
    Image,
    RichTextComponent
} from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    AttributeValue,
    CartLine,
    SalesLine
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import React, { useEffect, useState } from 'react';
import { brandCodeParser } from '../Utilities/brand-code-parser';
import { isLineSubscriptionLine } from '../Utilities/subscription-manager';
import { ElicitAmountSelector } from './elicit-amountselector.component';

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs
 */
export interface ICartlineResourceString {
    /** Display string for discount label */
    discountStringText: string;
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
    /** Display string for quantity label  */
    quantityDisplayString: string;
    /** Display string for quantity label  */
    inputQuantityAriaLabel: string;
}

interface ICartLineData {
    cartLine: CartLine | SalesLine;
    product: SimpleProduct;
}

/**
 * ICartLineProps: The props required to render cartLineitem
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    percentSaving: number;
    templateSavingText: string;
    templatePercentSavingText: string;
    /** The flag to change the quantity component from interactible to static  */
    disableQuantityInteractivity?: boolean;
    /** The primary image url  */
    primaryImageUrl?: string;
    /** The product url  */
    productUrl?: string;
    /** GridSettings for the product image in cartLine  */
    gridSettings: IGridSettings;
    /** ImageSettings for the product image in cartLine  */
    imageSettings: IImageSettings;
    /** Boolean flag to indicate if the item is out of stock  */
    isOutOfStock?: boolean;
    /** Flag to make quantity section editable  */
    isQuantityEditable?: boolean;
    /** Max quantity for line item  */
    maxQuantity?: number;
    /** Current quantity for line item  */
    currentQuantity?: number;
    /** Resource string for the component  */
    resources: ICartlineResourceString;
    /** SalesLine flag  */
    isSalesLine?: boolean;
    //   Remove button coming from smwe-cartline
    // remove:string;
    remove?: React.ReactNode;
    /** Display images for on cart lines  */
    showImages?: boolean;
    /** Has zero availability error?  */
    zeroAvailabilityError?: boolean;
    cartState?: ICartState;
    cartLineErrors?: { [index: string]: boolean };
    /** Replaces discount code name with percent  */
    showDiscountPercent?: boolean;
    /** Attributes for product  */
    productAttributes?: AttributeValue[];
    /** Quantity onChange callback  */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): void;
    cartLineOnChange?(ctx: IActionContext, cartState: ICartState | undefined, cartLine: CartLine): void;
}

// tslint:disable-next-line:max-func-body-length
const SmweCartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { isSalesLine, productUrl, resources, primaryImageUrl, showImages, gridSettings, imageSettings, zeroAvailabilityError } = props;
    const intlFormatter = props.context.cultureFormatter;
    const { product } = props.data;

    const _updateQuantityRaw = (num: number) => {
        if (props.quantityOnChange) {
            props.quantityOnChange(
                props.data.cartLine,
                num || 10
            );
        }
    };

    const _generateSelectMenu = (
        quantity: number,
        currentQuantity: number | undefined
    ): JSX.Element => {
        const shouldDisableQty = props.data.product && props.data.product.ItemId?.toLowerCase() === props.context.request.channel?.GiftCardItemId?.toLowerCase();
        return (
            <td className='msc-cart-line__item-quantity' tabIndex={0}>
                <ElicitAmountSelector
                    cartLineId={props.data.cartLine.LineId}
                    cart={props.cartState}
                    key={`${props.data.product.RecordId}${props.data.product.ItemId}`}
                    shouldDisableQty={shouldDisableQty}
                    zeroAvailabilityError={zeroAvailabilityError}
                    isSubLine={isLineSubscriptionLine(props.data.cartLine)}
                    // requires to be passed through
                    _updateQuantityRaw={_updateQuantityRaw}
                    currentQuantity={currentQuantity!}
                />
                {props.remove}
            </td>
        );
    };
    const _isWineProduct = (productId: string | undefined) => {
        return productId && /^\d+$/.test(productId);
    };

    const _generateQuantityAndPrice = (): JSX.Element[] | null => {
        const nodes = [];
        const priceAdjustmentSavings = _getPriceAdjustmentSavings();
        // TODO: Temporary solution till we get better solution in checking product type in Cart
        const isWineProduct = _isWineProduct(product.ItemId);
        if (isWineProduct && props.isOutOfStock && !zeroAvailabilityError) {
            return null;
        }

        if (props.isQuantityEditable) {
            nodes.push(
                _generateSelectMenu(props.maxQuantity || 10, props.currentQuantity)
            );
        } else {
            nodes.push(
                <td className='msc-cart-line__item-quantity' tabIndex={0}>
                    <label className='msc-quantity__label'>
                        {resources.quantityDisplayString}
                    </label>
                    <span className='msc-quantity__value'>
                        {props.data.cartLine.Quantity} {props.remove}
                    </span>
                </td>
            );
        }
        nodes.push(
            <td className='msc-cart-line__item-unitprice' tabIndex={0}>
                <div className='msc-price__value'>
                    {props.data.cartLine.Price !== undefined
                        ? intlFormatter.formatCurrency(props.data.cartLine.Price * (1 - ((props.data.cartLine.DiscountAmountWithoutTax || 0) / (props.data.cartLine.NetPrice || 0))))
                        : ''}
                    {_getPriceAdjustmentPercent()}
                </div>
                <div className='msc-price__discount'>
                    {_getPriceDiscount()}
                </div>
            </td>
        );
        let price = props.data.cartLine.NetPrice! / props.percentSaving;
        if (!isFinite(price)) {
            price = 0;
        }
        nodes.push(
            <td className='msc-cart-line__item-total' tabIndex={0}>
                <div className='msc-price__value'>
                    {isSalesLine
                        ? props.data.cartLine.Price && intlFormatter.formatCurrency(props.data.cartLine.Price)
                        : props.data.cartLine.TotalAmount === 0
                            ? intlFormatter.formatCurrency(0)
                            : props.data.cartLine.TotalAmount && intlFormatter.formatCurrency(props.data.cartLine.TotalAmount)
                    }
                </div>
                <div className='msc-price__discount'>
                    {(props.data.cartLine.DiscountAmountWithoutTax && props.data.cartLine.DiscountAmountWithoutTax > 0)
                        || (priceAdjustmentSavings && priceAdjustmentSavings > 0)
                        ? `${intlFormatter.formatCurrency((props.data.cartLine.DiscountAmountWithoutTax || 0) + _getPriceAdjustmentSavings())} savings`
                        : null
                    }
                </div>
            </td>
        );
        return nodes;
    };

    const _getCartLineImage = (): JSX.Element | null => {
        if (showImages && primaryImageUrl) {
            return (
                <div className='msc-cart-line__item-image'>
                    <Image
                        src={primaryImageUrl}
                        altText={product.Name || 'cart-line-image'}
                        gridSettings={gridSettings}
                        imageSettings={imageSettings}
                    />
                </div>
            );
        } else {
            return null;
        }
    };

    const _getPriceAdjustmentPercent = (): JSX.Element | null => {
        if (props.data.product.Price && props.data.cartLine.Price && (props.data.cartLine.Price < props.data.product.Price)) {
            return <span className='msc-cart-line__price-adjusted-percent'>{` (${((props.data.cartLine.Price / props.data.product.Price) * 100).toFixed(0)}% Off)`}</span>;
        } else {
            return null;
        }
    };

    const _getPriceDiscount = (): string | null => {
        if (
            props.data.cartLine.DiscountAmountWithoutTax !== undefined &&
            props.data.cartLine.DiscountAmountWithoutTax !== 0 &&
            props.data.cartLine.Quantity &&
            props.data.cartLine.DiscountLines
        ) {
            if (props.showDiscountPercent && props.data.cartLine.NetPrice) {
                return `${((props.data.cartLine.DiscountAmountWithoutTax / props.data.cartLine.NetPrice) * 100).toFixed(0)}% off`;
            }
            return `${_doubleDiscounts()} ${props.data.cartLine.DiscountLines[0].DiscountCode}`;

        }
        return null;
    };

    enum GiftCardFieldType {
        Extension, Attribute
    }

    interface IGiftCardFields {
        Key: string;
        Type: GiftCardFieldType;
        StringValue: string;
        Required: boolean;
        isValid?: boolean;
    }

    interface IGiftCardFormObj {
        FirstName: IGiftCardFields;
        LastName: IGiftCardFields;
        ElectronicDeliveryEmail: IGiftCardFields;
        ElectronicDeliveryEmailContent: IGiftCardFields;
    }

    const giftCardFormObj: IGiftCardFormObj = {
        FirstName: {
            Key: 'FirstName',
            Type: GiftCardFieldType.Extension,
            StringValue: '',
            Required: true,
            isValid: true
        },
        LastName: {
            Key: 'LastName',
            Type: GiftCardFieldType.Extension,
            StringValue: '',
            Required: true,
            isValid: true
        },
        ElectronicDeliveryEmail: {
            Key: 'ElectronicDeliveryEmail',
            Type: GiftCardFieldType.Attribute,
            StringValue: '',
            Required: true,
            isValid: true
        },
        ElectronicDeliveryEmailContent: {
            Key: 'ElectronicDeliveryEmailContent',
            Type: GiftCardFieldType.Attribute,
            StringValue: '',
            Required: false
        },
    };

    // tslint:disable-next-line: max-func-body-length
    const _renderGiftCardPrompt = (): JSX.Element | null => {

        if (!props.data.cartLine.IsGiftCardLine) {
            return null;
        }

        const [giftCardFormField, setGiftCardFormField] = useState(giftCardFormObj);
        const [formFieldsDebounce, setFormFieldsDebounce] = useState('');

        const setFormFieldState = () => {
            const unhydratedCartLineLoad = { ...props.data.cartLine };
            Object.keys(giftCardFormField).forEach((field) => {
                const fieldState = giftCardFormField[field];
                if (fieldState.Type === GiftCardFieldType.Extension) {
                    unhydratedCartLineLoad.ExtensionProperties = unhydratedCartLineLoad.ExtensionProperties || [];
                    const property = unhydratedCartLineLoad.ExtensionProperties.find((extension) => {
                        return extension.Key?.toLowerCase() === fieldState.Key?.toLowerCase();
                    });
                    if (property) {
                        setGiftCardFormField((prevState: IGiftCardFormObj) => ({
                            ...prevState,
                            [field]: {
                                ...prevState[field],
                                StringValue: property.Value?.StringValue
                            }
                        }));
                    }
                } else if (fieldState.Type === GiftCardFieldType.Attribute) {
                    const property = unhydratedCartLineLoad[fieldState.Key];
                    if (property) {
                        setGiftCardFormField((prevState: IGiftCardFormObj) => ({
                            ...prevState,
                            [fieldState.Key]: {
                                ...prevState[fieldState.Key],
                                StringValue: unhydratedCartLineLoad[fieldState.Key]
                            }
                        }));
                    }
                }
            });
        };

        const updateFormFieldToCartLine = () => {
            const unhydratedCartLineLoad = { ...props.data.cartLine };
            Object.keys(giftCardFormField).forEach((field) => {
                const fieldState = giftCardFormField[field];
                if (fieldState.Type === GiftCardFieldType.Extension) {
                    unhydratedCartLineLoad.ExtensionProperties = unhydratedCartLineLoad.ExtensionProperties || [];
                    unhydratedCartLineLoad.ExtensionProperties.push({
                        Key: fieldState.Key,
                        Value: {
                            StringValue: fieldState.StringValue
                        }
                    });
                } else if (fieldState.Type === GiftCardFieldType.Attribute) {
                    unhydratedCartLineLoad[fieldState.Key] = giftCardFormField[fieldState.Key].StringValue;
                }
            });
            // Removing 'ItemTaxGroupId' from the object because now in new SSK its READONLY Attribute, can't be updated.
            delete (unhydratedCartLineLoad.ItemTaxGroupId);
            props.cartLineOnChange && props.cartLineOnChange(props.context.actionContext, props.cartState, unhydratedCartLineLoad);
        };

        const giftCardValidation = () => {
            let giftCardFormIsValid = true;
            Object.keys(giftCardFormField).forEach((field) => {
                const fieldState = giftCardFormField[field];
                if (fieldState.Required) {
                    if (fieldState.StringValue.trim() === '') {
                        giftCardFormIsValid = false;
                        fieldState.isValid = false;
                    } else {
                        fieldState.isValid = true;
                    }

                    if (fieldState.Key === 'ElectronicDeliveryEmail') {
                        const validEmailRegex =
                            RegExp(/^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i);
                        if (!validEmailRegex.test(fieldState.StringValue)) {
                            giftCardFormIsValid = false;
                            fieldState.isValid = false;
                        } else {
                            fieldState.isValid = true;
                        }
                    }
                }
            });
            if (props.cartLineErrors) {
                if (giftCardFormIsValid) {
                    props.cartLineErrors[props.data.cartLine.LineId!] = false;
                } else {
                    props.cartLineErrors[props.data.cartLine.LineId!] = true;
                }
            }
        };

        // Setting the data from the cartline to State on load
        useEffect(() => {
            setFormFieldState();
        }, []);

        //Checking giftCard form validation
        useEffect(() => {
            giftCardValidation();
        }, [giftCardFormField]);

        // Updating formfield to cardLine
        useEffect(() => {
            const delayDebounceFn = setTimeout(() => {
                updateFormFieldToCartLine();
            }, 600);

            return () => clearTimeout(delayDebounceFn);
        }, [formFieldsDebounce]);

        const handleChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value = event?.target.value;
            if (name) {
                setGiftCardFormField((prevState: IGiftCardFormObj) => ({
                    ...prevState,
                    [name]: {
                        ...prevState[name],
                        StringValue: value,
                    },
                }));
                setFormFieldsDebounce(value);
            }
        };

        return (
            <div className='form-row gift-cart-form'>
                <div className='col-12 col-md-7 col-lg-6'>
                    <div className='form-row'>
                        <div className='form-group col-md-6'>
                            <label>Recipient’s First Name*</label>
                            <input type='text' className={`form-control  ${!giftCardFormField.FirstName.isValid && 'is-invalid'}`} value={giftCardFormField.FirstName.StringValue} onChange={handleChange('FirstName')} />
                        </div>
                        <div className='form-group col-md-6'>
                            <label>Last Name*</label>
                            <input type='text' className={`form-control  ${!giftCardFormField.LastName.isValid && 'is-invalid'}`} value={giftCardFormField.LastName.StringValue} onChange={handleChange('LastName')} />
                        </div>
                    </div>
                    <div className='form-row'>
                        <div className='form-group col-12'>
                            <p>IMPORTANT: Gift Cards are sent to the recipient email address the same day orders are placed. You can use your own e-mail address to keep it a surprise.</p>
                            <label>Email Address*</label>
                            <input
                                type='text'
                                className={`form-control  ${!giftCardFormField.ElectronicDeliveryEmail.isValid && 'is-invalid'}`}
                                // @ts-ignore
                                value={giftCardFormField.ElectronicDeliveryEmail.StringValue}
                                onChange={handleChange('ElectronicDeliveryEmail')}
                            />
                        </div>
                    </div>
                    <div className='form-row'>
                        <div className='col-12'>
                            <label>Gift Message</label>
                            <textarea
                                className='form-control'
                                value={giftCardFormField.ElectronicDeliveryEmailContent.StringValue}
                                onChange={handleChange('ElectronicDeliveryEmailContent')}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const _getCartLineComment = (): JSX.Element | null => {
        if (_isWineProduct(props.data.product.ItemId) && props.data.cartLine.Comment) {
            return (
                <div className='msc-cart-line__item-error'>
                    <span>{props.data.cartLine.Comment}</span>
                </div>);
        } else {
            return null;
        }
    };

    const _getPriceAdjustmentSavings = (): number => {
        if (props.data.product.Price && props.data.cartLine.Price && props.data.cartLine.NetPrice) {
            const priceUnadjusted = (props.data.cartLine.NetPrice / (props.data.cartLine.Price / props.data.product.Price));
            return (priceUnadjusted - props.data.cartLine.NetPrice);
        } else {
            return 0;
        }
    };

    const _doubleDiscounts = (): string => {
        if (_getPriceAdjustmentPercent()) {
            return '+ ';
        } else {
            return '';
        }
    };

    const _getKitDescription = (): JSX.Element | null => {
        if (props.productAttributes) {
            const productType = props.productAttributes.find(attribute => attribute.Name === 'Product Type');
            if (productType?.TextValue === 'Assembly') {
                const description = props.productAttributes.find(
                    attribute => attribute.Name === 'Includes'
                )?.TextValue;
                return (
                    <div className='msc-cart-line__item-kit'>
                        <RichTextComponent text={description as string || ''} />
                    </div>
                );
            }
        }
        return null;
    };

    const brandCode = brandCodeParser({ description: product.Description, appContext: props.context });

    return (
        <>
            <tr className='msc-cart-line__item'>
                <td tabIndex={0}>
                    <a className='msc-cart-line__item-details' href={productUrl}>
                        {_getCartLineImage()}
                        <div className='msc-cart-line__item-heading'>
                            {brandCode &&
                                <div className='msc-cart-line__item-brand'>
                                    {brandCode}
                                </div>
                            }
                            <div className='msc-cart-line__item-name'>
                                {product.Name}
                            </div>
                            {_getKitDescription()}
                        </div>
                    </a>
                </td>
                {_generateQuantityAndPrice()}
            </tr>
            {_getCartLineComment()}
            {_renderGiftCardPrompt()}
        </>
    );
};

// @ts-ignore
export default SmweCartLine;
